import { useState, useCallback } from 'react';
import type { BillingPlan, CreateBillingPlanDto } from '@yawi/types';
import { Wizard, type WizardStep } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';
import { BasicInfoStep, PricingStep, LimitsStep, FeaturesStep, SummaryStep } from './steps';
import { initialPlanData, type PlanWizardData } from './types';

const STEPS: WizardStep[] = [
  { id: 'basic', label: 'Información' },
  { id: 'pricing', label: 'Precio' },
  { id: 'limits', label: 'Límites' },
  { id: 'features', label: 'Características' },
  { id: 'summary', label: 'Resumen' },
];

interface PlanWizardProps {
  plan?: BillingPlan;
  onClose: () => void;
  onSuccess: () => void;
}

export function PlanWizard({ plan, onClose, onSuccess }: PlanWizardProps) {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const isEditing = !!plan;

  const getInitialData = (): PlanWizardData => {
    if (plan) {
      return {
        code: plan.code,
        name: plan.name,
        description: plan.description ?? '',
        billingCycle: plan.billingCycle,
        basePrice: plan.basePrice,
        includedUnits: plan.includedUnits,
        includedUsers: plan.includedUsers,
        includedStorageBytes: plan.includedStorageBytes,
        includedNotifications: plan.includedNotifications,
        extraUnitPrice: plan.extraUnitPrice,
        extraUserPrice: plan.extraUserPrice,
        extraStoragePricePerGb: plan.extraStoragePricePerGb,
        extraNotificationPrice: plan.extraNotificationPrice,
        trialDays: plan.trialDays,
        isActive: plan.isActive,
        isPublic: plan.isPublic,
        sortOrder: plan.sortOrder,
        features: plan.features ?? {},
      };
    }
    return initialPlanData;
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<PlanWizardData>(getInitialData);
  const [stepValidity, setStepValidity] = useState<boolean[]>([
    !!plan?.name, // Basic info valid if has name (editing) or not
    true, // Pricing always valid
    true, // Limits always valid
    true, // Features always valid
    true, // Summary always valid
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDataChange = useCallback((newData: Partial<PlanWizardData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const handleValidChange = useCallback((isValid: boolean) => {
    setStepValidity((prev) => {
      const newValidity = [...prev];
      newValidity[currentStep] = isValid;
      return newValidity;
    });
  }, [currentStep]);

  const handleStepClick = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentStep((s) => Math.max(0, s - 1));
  }, []);

  const handleNext = useCallback(() => {
    if (stepValidity[currentStep]) {
      setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1));
    }
  }, [currentStep, stepValidity]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const planData: CreateBillingPlanDto = {
        code: data.code,
        name: data.name,
        description: data.description,
        billingCycle: data.billingCycle,
        basePrice: data.basePrice,
        includedUnits: data.includedUnits,
        includedUsers: data.includedUsers,
        includedStorageBytes: data.includedStorageBytes,
        includedNotifications: data.includedNotifications,
        extraUnitPrice: data.extraUnitPrice,
        extraUserPrice: data.extraUserPrice,
        extraStoragePricePerGb: data.extraStoragePricePerGb,
        extraNotificationPrice: data.extraNotificationPrice,
        trialDays: data.trialDays,
        isActive: data.isActive,
        isPublic: data.isPublic,
        sortOrder: data.sortOrder,
        features: data.features,
      };

      if (isEditing) {
        await billingRepository.updatePlan(plan.id, planData);
        showToast('success', 'Plan actualizado correctamente');
      } else {
        await billingRepository.createPlan(planData);
        showToast('success', 'Plan creado correctamente');
      }

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Error al ${isEditing ? 'actualizar' : 'crear'} el plan`;
      showToast('error', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const stepProps = {
      data,
      onChange: handleDataChange,
      onValidChange: handleValidChange,
    };

    switch (currentStep) {
      case 0:
        return <BasicInfoStep {...stepProps} />;
      case 1:
        return <PricingStep {...stepProps} />;
      case 2:
        return <LimitsStep {...stepProps} />;
      case 3:
        return <FeaturesStep {...stepProps} />;
      case 4:
        return <SummaryStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <Wizard
      title={isEditing ? 'Editar Plan' : 'Nuevo Plan'}
      subtitle={isEditing
        ? `Modificar ${plan.name}`
        : 'Configura un nuevo plan de suscripción'
      }
      steps={STEPS}
      currentStep={currentStep}
      stepValidity={stepValidity}
      onStepClick={handleStepClick}
      onPrev={handlePrev}
      onNext={handleNext}
      onSubmit={handleSubmit}
      onClose={onClose}
      canGoNext={stepValidity[currentStep]}
      isSubmitting={isSubmitting}
      submitLabel={isEditing ? 'Guardar Cambios' : 'Crear Plan'}
    >
      {renderStep()}
    </Wizard>
  );
}
